import React, {useEffect, useMemo, useRef, useState} from "react";
import {Alert, Button, Select, Switch, Table, Tabs, Tag, Tooltip, message} from "antd";
import {BarChartOutlined, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {ApiLinksService} from "../../../../../shared/api";
import {useCanEdit} from "../../../sectionAccess";
import {authModel} from "../../../../../entities/auth";
import {SectionToolbar, countText, filterItems} from "../../components/SectionToolbar/SectionToolbar";
import {CopyLine, STEPS, errorOf, showStep, stepValue} from "./linkParts";
import {LinkForm} from "./LinkForm";
import {LinkStats} from "./LinkStats";
import {DomainsTab} from "./DomainsTab";

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('ru-RU') : '')
const STATS_FRESH_MS = 3 * 60 * 1000

// «Генератор ссылок»: links with UTM for mailings, their short form and funnel; the sites they lead to
export const LinksSection = () => {
  const canEdit = useCanEdit()
  const [links, setLinks] = useState([])
  const [domains, setDomains] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [domainId, setDomainId] = useState(undefined)
  const [archived, setArchived] = useState(false)
  const [creating, setCreating] = useState(false)
  const [opened, setOpened] = useState(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const alive = useRef(true)

  useEffect(() => () => {
    alive.current = false
  }, [])

  const replace = (link) => setLinks((list) => list.map((l) => (l.id === link.id ? {...l, ...link} : l)))

  // the funnel of the links on the screen whose numbers are old (the server keeps them 10 minutes)
  const refreshStats = async (list, force = false) => {
    const due = list.filter((l) => !l.archived && l.metrika && (force || !l.statsAt || Date.now() - new Date(l.statsAt).getTime() > STATS_FRESH_MS))
    if (!due.length) return
    setStatsLoading(true)
    for (const link of due.slice(0, 30)) {
      if (!alive.current) return
      try {
        const {data} = await ApiLinksService.fetchStats(link.id, force)
        replace(data)
      } catch (e) {
        message.error(errorOf(e).message || 'Метрика не ответила')
        break
      }
    }
    if (alive.current) setStatsLoading(false)
  }

  const load = () => {
    setLoading(true)
    Promise.all([ApiLinksService.fetchAll(), ApiLinksService.fetchDomains()])
      .then(([l, d]) => {
        setLinks(l.data)
        setDomains(d.data)
        setError(null)
        refreshStats(l.data)
      })
      .catch(() => setError('Не удалось загрузить ссылки. Обновите страницу.'))
      .finally(() => setLoading(false))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [])

  const shown = useMemo(() => filterItems(links, search, ['title', 'description', 'longUrl', 'shortUrl', (l) => Object.values(l.utm)])
    .filter((l) => l.archived === archived && (!domainId || l.domainId === domainId)), [links, search, domainId, archived])

  const columns = [
    {
      title: 'Ссылка',
      key: 'title',
      width: 280,
      render: (link) => (
        <>
          <b>{link.title}</b>
          {link.description && (
            <Tooltip title={<span style={{whiteSpace: 'pre-wrap'}}>{link.description}</span>} overlayStyle={{maxWidth: 520}}>
              <div className='link-description'>{link.description}</div>
            </Tooltip>
          )}
          <div className='cell-hint'>{link.host} · {formatDate(link.createdAt)}{link.createdBy ? ` · ${link.createdBy}` : ''}</div>
          <div className='tag-list' style={{marginTop: 4}}>
            {Object.entries(link.utm).filter(([, v]) => v).map(([k, v]) => <Tag key={k}>{k.replace('utm_', '')}: {v}</Tag>)}
          </div>
        </>
      ),
    },
    {
      title: 'Адрес',
      key: 'urls',
      width: 300,
      render: (link) => (
        <>
          {link.shortUrl && <CopyLine label='короткая' url={link.shortUrl}/>}
          <CopyLine label={link.shortUrl ? 'длинная' : null} url={link.longUrl}/>
        </>
      ),
    },
    ...STEPS.map((step) => ({
      title: <Tooltip title={step.hint}>{step.title}</Tooltip>,
      key: step.key,
      align: 'right',
      width: 84,
      render: (link) => {
        const value = stepValue(link, step.key)
        return value === null ? <span className='cell-empty'>—</span> : <b>{showStep(step, value)}</b>
      },
    })),
    {
      title: '',
      key: 'open',
      width: 60,
      render: (link) => <Button icon={<BarChartOutlined/>} title='Статистика по дням' onClick={() => setOpened(link)}/>,
    },
  ]

  const linksTab = (
    <>
      <Alert
        className='section-note'
        type='info'
        showIcon
        message='Ссылка для рассылки: сайт, страница и UTM. Статистика — из Яндекс Метрики сайта по UTM этой ссылки, поэтому адрес и UTM после создания не меняются. «Кликнули» считаем только у коротких ссылок: длинная ведёт на сайт напрямую.'
      />
      {error && <Alert className='section-note' type='error' message={error}/>}
      <Table
        rowKey='id'
        title={() => (
          <SectionToolbar
            search={search}
            onSearch={setSearch}
            placeholder='Название, описание, UTM или адрес'
            count={countText(links.filter((l) => l.archived === archived).length, shown.length, search)}
            filters={(
              <>
                <Select allowClear style={{width: 200}} placeholder='Все сайты' value={domainId} onChange={setDomainId}
                  options={domains.map((d) => ({value: d.id, label: d.host}))}/>
                <label className='links-archived'>
                  <Switch size='small' checked={archived} onChange={setArchived}/> архив
                </label>
              </>
            )}
          >
            <Button icon={<ReloadOutlined/>} loading={statsLoading} onClick={() => refreshStats(shown, true)}>Обновить статистику</Button>
            {canEdit && <Button type='primary' icon={<PlusOutlined/>} onClick={() => setCreating(true)}>Создать ссылку</Button>}
          </SectionToolbar>
        )}
        columns={columns}
        dataSource={shown}
        loading={loading}
        sticky
        scroll={{x: 1300}}
        pagination={{pageSize: 30, showSizeChanger: false, hideOnSinglePage: true}}
        locale={{emptyText: archived ? 'В архиве пусто' : 'Ссылок пока нет'}}
      />
    </>
  )

  return (
    <>
      <Tabs defaultActiveKey='links'>
        <Tabs.TabPane tab='Ссылки' key='links'>{linksTab}</Tabs.TabPane>
        <Tabs.TabPane tab='Сайты' key='domains'>
          {/* sites and Metrika counters: administrators only (the token sees every counter of the account) */}
          <DomainsTab domains={domains} canEdit={canEdit && authModel.isAdmin} onChanged={load}/>
        </Tabs.TabPane>
      </Tabs>
      {creating && (
        <LinkForm
          domains={domains}
          onClose={() => setCreating(false)}
          onCreated={(link) => {
            setLinks((list) => [link, ...list])
            setCreating(false)
            setOpened(link)
          }}
        />
      )}
      {opened && (
        <LinkStats
          link={links.find((l) => l.id === opened.id) || opened}
          canEdit={canEdit}
          onClose={() => setOpened(null)}
          onChanged={replace}
        />
      )}
    </>
  )
}
